import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, X, EyeOff } from "lucide-react";
import {
  formatMoney,
  BANK_STATEMENT_LINE_STATUS_LABELS,
  BANK_STATEMENT_LINE_STATUS_TONE,
} from "@finora/config";
import { BankStatementLineStatus, type BankStatementLineDto } from "@finora/types";
import { Button, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";

function BookLineModal({
  line,
  onClose,
}: {
  line: BankStatementLineDto;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: candidates, isLoading } = useQuery({
    queryKey: ["bank-statement-candidates", line.id],
    queryFn: () => api.getBankStatementLineCandidates(line.id),
  });

  const book = useMutation({
    mutationFn: (documentId: string) => api.bookBankStatementLine(line.id, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-statement"] });
      onClose();
    },
  });

  const sorted = [...(candidates ?? [])].sort((a, b) => Number(b.isSuggested) - Number(a.isSuggested));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Proknjiži uplatu</h2>
            <p className="text-sm text-slate-500">
              {line.payerName ?? "Nepoznat uplatilac"} · {formatMoney(line.amount)}
            </p>
          </div>
          <button
            type="button"
            aria-label="Zatvori"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <p className="p-5 text-sm text-slate-500">Učitavanje...</p>
          ) : sorted.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">Nema otvorenih faktura za uparivanje.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Broj</th>
                  <th>Kupac</th>
                  <th>Preostalo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => (
                  <tr key={c.id} className={c.isSuggested ? "bg-emerald-50/50" : undefined}>
                    <td className="font-medium text-slate-800">
                      {c.documentNumber ?? "(nacrt)"}
                      {c.isSuggested && (
                        <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          Predloženo
                        </span>
                      )}
                    </td>
                    <td className="text-slate-500">{c.customerName ?? "—"}</td>
                    <td>{formatMoney(c.dueAmount)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant={c.isSuggested ? "success" : "secondary"}
                        disabled={book.isPending}
                        onClick={() => book.mutate(c.id)}
                      >
                        Izaberi
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function BankStatementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bookingLine, setBookingLine] = useState<BankStatementLineDto | null>(null);

  const { data: statement, isLoading } = useQuery({
    queryKey: ["bank-statement", id],
    queryFn: () => api.getBankStatement(id!),
    enabled: !!id,
  });

  const ignore = useMutation({
    mutationFn: (lineId: string) => api.ignoreBankStatementLine(lineId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bank-statement", id] }),
  });

  const bookAllSuggested = useMutation({
    mutationFn: async () => {
      const candidates = (statement?.lines ?? []).filter(
        (l) => l.direction === "CREDIT" && l.status === BankStatementLineStatus.SUGGESTED && l.matchedDocumentId,
      );
      for (const line of candidates) {
        await api.bookBankStatementLine(line.id, line.matchedDocumentId!);
      }
      return candidates.length;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bank-statement", id] }),
  });

  const approve = useMutation({
    mutationFn: () => api.approveBankStatement(id!),
    onSuccess: (updated) => {
      // Odmah prikaži "odobren" bez osvežavanja strane.
      queryClient.setQueryData(["bank-statement", id], updated);
      queryClient.invalidateQueries({ queryKey: ["bank-statement", id] });
      queryClient.invalidateQueries({ queryKey: ["bank-statements"] });
    },
  });

  if (isLoading || !statement) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/izvodi")}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na Izvodi
      </button>

      {approve.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">Odobravanje nije uspelo. Pokušajte ponovo.</p>
      )}
      {approve.isSuccess && statement.reviewStatus === "APPROVED" && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
          <Check className="h-4 w-4" aria-hidden />
          Izvod je odobren.
        </div>
      )}

      {statement.reviewStatus === "PENDING_REVIEW" && (
        <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-amber-800">
              Ovaj izvod je stigao preko API-ja i čeka pregled
            </p>
            <p className="text-xs text-amber-700">
              Proveri stavke ispod pre nego što potvrdiš - dok ne odobriš, izvod se tretira kao nacrt.
            </p>
          </div>
          <Button disabled={approve.isPending} onClick={() => approve.mutate()}>
            <Check className="h-4 w-4" aria-hidden />
            {approve.isPending ? "Odobravam..." : "Odobri"}
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Izvod {statement.statementNumber ?? ""}
          </h1>
          <p className="text-sm text-slate-500">
            {new Date(statement.statementDate).toLocaleDateString("sr-RS")} · Prethodno stanje{" "}
            {formatMoney(statement.previousBalance)} → Novo stanje {formatMoney(statement.newBalance)}
          </p>
        </div>
        {statement.lines.some(
          (l) => l.direction === "CREDIT" && l.status === BankStatementLineStatus.SUGGESTED && l.matchedDocumentId,
        ) && (
          <Button
            variant="success"
            disabled={bookAllSuggested.isPending}
            onClick={() => bookAllSuggested.mutate()}
          >
            <Check className="h-4 w-4" aria-hidden />
            {bookAllSuggested.isPending ? "Knjižim..." : "Proknjiži sve predložene"}
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <table>
          <thead>
            <tr>
              <th>Datum valute</th>
              <th>Uplatilac</th>
              <th>Opis</th>
              <th>Poziv na broj</th>
              <th>Iznos</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {statement.lines.map((line) => (
              <tr key={line.id}>
                <td className="text-slate-500">
                  {new Date(line.valueDate).toLocaleDateString("sr-RS")}
                </td>
                <td className="font-medium text-slate-800">{line.payerName ?? "—"}</td>
                <td className="max-w-xs truncate text-slate-500" title={line.description ?? ""}>
                  {line.description ?? "—"}
                </td>
                <td className="text-slate-500">{line.referenceNumber ?? "—"}</td>
                <td className={line.direction === "CREDIT" ? "font-medium text-emerald-700" : "text-slate-500"}>
                  {line.direction === "CREDIT" ? "+" : "-"}
                  {formatMoney(line.amount)}
                </td>
                <td>
                  {line.direction === "DEBIT" && line.status === "UNMATCHED" ? (
                    <span className="text-slate-400" title="Odliv - ne knjiži se, nema uparivanja">
                      →
                    </span>
                  ) : (
                    <StatusBadge
                      label={BANK_STATEMENT_LINE_STATUS_LABELS[line.status]}
                      tone={BANK_STATEMENT_LINE_STATUS_TONE[line.status]}
                    />
                  )}
                  {line.matchedDocumentNumber && (
                    <span className="ml-1 text-xs text-slate-400">
                      ({line.matchedDocumentNumber})
                    </span>
                  )}
                </td>
                <td>
                  {line.direction === "CREDIT" &&
                    (
                      [BankStatementLineStatus.UNMATCHED, BankStatementLineStatus.SUGGESTED] as BankStatementLineStatus[]
                    ).includes(line.status) && (
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => setBookingLine(line)}>
                          <Check className="h-3.5 w-3.5" aria-hidden /> Proknjiži
                        </Button>
                        <button
                          aria-label="Ignoriši stavku"
                          onClick={() => ignore.mutate(line.id)}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"
                        >
                          <EyeOff className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bookingLine && <BookLineModal line={bookingLine} onClose={() => setBookingLine(null)} />}
    </div>
  );
}
